"use client";

import useSizeController from '@/hooks/useSizeController';
import { faX } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { motion, useAnimate, useDragControls, useMotionValue } from "framer-motion";
import { ReactNode, useEffect, useState } from "react";
import { createPortal } from "react-dom";
import useMeasure from "react-use-measure";

const ModalInMobile = ({ children, show, title, customButton, onClose, zIndex = 120 }:
    { zIndex?: number; result?: string; children: ReactNode; show: boolean; title?: ReactNode; customButton?: ReactNode; onClose?: () => void; }) => {
    const [showModal, setShowModal] = useState(false);
    const [scope, animate] = useAnimate();
    const [drawerRef, { height, width }] = useMeasure();
    const { isSmall } = useSizeController(640)
    const y = useMotionValue(0);
    const controls = useDragControls();

    const handleClose = async () => {
        animate(scope.current, { opacity: [1, 0] });

        const startValue = typeof y.get() === "number" ? y.get() : 0;

        await animate("#drawer", isSmall ? { y: [startValue, height] } : { x: [startValue, -width] });

        setShowModal(false);
        onClose && onClose();
    };
    useEffect(() => {
        setShowModal(show);
    }, [show]);

    useEffect(() => {
        if (showModal && typeof window !== 'undefined') {
            document.body.classList.add("overflow-hidden");
        } else if (typeof window !== 'undefined') {
            document.body.classList.remove("overflow-hidden");
        }

        return () => document.body.classList.remove("overflow-hidden");
    }, [showModal]);

    return (
        showModal &&
        createPortal(
            <motion.div

                ref={scope}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                onClick={handleClose}
                className={`fixed inset-0 bg-neutral-950/70 z-[120]`}
                style={{ zIndex }}
            >
                <motion.div

                    id="drawer"
                    ref={drawerRef}
                    onClick={(e) => e.stopPropagation()}
                    initial={isSmall ? { y: "100%" } : { x: "-100%" }}
                    animate={isSmall ? { y: "0%" } : { x: "0%" }}

                    transition={{
                        ease: "easeInOut",
                    }}
                    className="small-scroller sm:!h-full absolute bottom-0 flex w-full flex-col items-center overflow-hidden sm:overflow-visible rounded-t-3xl
                     bg-white sm:max-w-[380px] sm:rounded-none sm:rounded-r-xl"
                    style={isSmall ? { y } : { x: y }}
                    drag="y"
                    dragControls={controls}
                    onDragEnd={() => {
                        if (y.get() >= 10) {
                            handleClose();
                        }
                    }}
                    dragListener={false}
                    dragConstraints={{
                        top: 0,
                        bottom: 0,
                    }}
                    dragElastic={{
                        top: 0,
                        bottom: 0.5,
                    }}
                >
                    <div onPointerDown={(e) => {
                        controls.start(e);
                    }} className="absolute left-0 right-0 top-0  z-10 flex flex-col justify-center items-center bg-white py-2 sm:hidden">
                        <button className="my-0.5 h-1 w-12 cursor-grab touch-none rounded-full bg-neutral-200 active:cursor-grabbing"></button>
                    </div>
                    {title && <h3 className='text-sm text-neutral-700 font-bold py-2 text-center border-b border-neutral-200 mt-4 w-full mx-4'>{title}</h3>}
                    <div className="flex w-full justify-between pt-1  ">
                        <div className=" hidden items-center gap-2 text-sm sm:flex">
                            <button className="w-fit p-2 sm:absolute top-4 sm:left-full sm:p-3 sm:rounded-r-lg sm:bg-white" onClick={handleClose}>
                                <FontAwesomeIcon icon={faX} />
                            </button>
                            <h3 className="text-sm font-semibold">{title}</h3>
                        </div>
                        <button className="mr-3 text-sm text-slate-600">
                            {customButton}
                        </button>
                    </div>
                    <div className="small-scroller h-full w-full overflow-y-scroll px-2 pb-2 pt-3 sm:pt-0 ">
                        {children}
                    </div>
                </motion.div>
            </motion.div>
            ,
            document.body,
        )
    );
};

export default ModalInMobile;
